import React, { useState, useMemo, useEffect } from "react";
import Modal from "components/Common/Modal";
import { useBinderAction, BinderRequestParams } from "./hook";
import { usePersonalBinder } from "../../List/Components/PersonalBinderList/hook";
import styles from "./style.scss";
import Button from "components/Common/Button/Text";
import { useNavigate, useLocation } from "react-router";
import { useToast } from "hooks/useToast";
import { BinderActionType } from "models/BinderItem";
import { assertUnreachable } from "utils/ts/typeAssertions";
import TextInput from "components/Common/TextInput";
import { BinderForm } from "../BinderForm";
import { CloseConfirmModal } from "../CloseConfirmModal";
import { trackAction } from "utils/helper/analytics";
import { useBinderItem } from "features/BinderPage/Item/hook";
import { AdvancedSearch } from "features/AboutSearch/AdvancedSearch";
import OverseasPremiumModal from "features/OverseasPremium/DetailModal";

const modalTexts = {
  delete: {
    title: "コレクションの削除",
    detail:
      "・一度削除したコレクションは復元できません。\n・共有中のコレクションを削除した場合、共有先のユーザーはこれ\nらを参照できなくなります。",
    button: "削除"
  },
  clone: {
    title: "コレクションの複製",
    detail:
      "複製を行うとフィード設定や保存コンテンツを引き継いだコレクションを作成します。\n複製元のコレクションの共有設定は新たなコレクションには引き継がれません。",
    button: "複製"
  },
  share: {
    title: "コレクションの共有",
    button: "共有"
  },
  edit: {
    title: "編集",
    button: "保存"
  },
  new: {
    title: "コレクションを作成",
    button: "新規作成"
  },
  publish: {
    title: "コレクションの公開",
    button: "保存"
  }
};

const getModalType = (type: BinderActionType) => {
  switch (type) {
    case "delete":
    case "delete_return_index":
      return "delete";
    case "clone":
    case "clone_show_new":
      return "clone";
    case "new":
      return "new";
    case "share":
      return "share";
    case "edit":
      return "edit";
    case "publish":
      return "publish";
  }
  assertUnreachable(type);
};

interface Props {
  onClose?: () => void;
  handleSave?: (params: BinderRequestParams) => void;
  isSaveLoading?: boolean;
  isOpen?: boolean;
  isHome?: boolean;
  errors?: string[];
}

export const BinderActionModal: React.FunctionComponent<Props> = ({
  onClose = () => {},
  handleSave = undefined,
  isSaveLoading = false,
  isOpen = null,
  isHome = false,
  errors = []
}) => {
  const {
    binderActionState,
    closeBinderActionModal,
    cloneBinder,
    shareBinder,
    publishBinder,
    createBinder,
    updateBinder,
    cloneCaption,
    setCloneCaption,
    limit
  } = useBinderAction({ isHome });

  const isOpenModal = useMemo(() => {
    if (isOpen !== null) {
      return isOpen;
    } else {
      return binderActionState !== null;
    }
  }, [binderActionState, isOpen]);

  const { fetchBinderList } = useBinderItem(
    binderActionState?.binderInfo?.binderId ?? ""
  );

  const { fetchPersonalBinders, deletePersonalBinder } = usePersonalBinder({
    isHome
  });

  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [isOpenCompanyModal, setIsOpenCompanyModal] = useState(false);
  const [isOpenImportModal, setIsOpenImportModal] = useState(false);

  const [definitionError, setDefinitionError] = useState("");
  const [backgroundError, setBackgroundError] = useState("");
  const [otherError, setOtherError] = useState("");
  const [taskError, setTaskError] = useState("");
  const [technologyError, setTechnologyError] = useState("");
  const [topicsError, setTopicsError] = useState("");
  const [regulationError, setRegulationError] = useState("");
  const [reOpenFlag, setReOpenFlag] = useState(false);
  const [notDeletable, setNotDeletable] = useState(true);
  const [isOpenHelp, setIsOpenHelp] = useState(false);
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);

  const setErrors = (messages: string[] | undefined) => {
    if (!messages) return;
    messages.forEach(message => {
      if (message.includes("定義")) {
        setDefinitionError(message);
      } else if (message.includes("背景")) {
        setBackgroundError(message);
      } else if (message.includes("課題")) {
        setTaskError(message);
      } else if (message.includes("技術")) {
        setTechnologyError(message);
      } else if (message.includes("話題")) {
        setTopicsError(message);
      } else if (message.includes("法規制")) {
        setRegulationError(message);
      } else {
        setOtherError(message);
      }
    });
  };

  const resetErrors = () => {
    setDefinitionError("");
    setBackgroundError("");
    setTaskError("");
    setTechnologyError("");
    setTopicsError("");
    setRegulationError("");
    setOtherError("");
  };

  const getSaveAtlasInfo = (binderInfo: BinderRequestParams) => {
    let count = 0;
    let register = "";
    let feedConditions = "";

    // 企業数の統計
    if (binderInfo.corpCode) {
      count = binderInfo.corpCode.split("|").length;
    }

    // 「企業選択」 or 「Excelインポート」を設定
    if (binderInfo.useMode === "select") {
      register = "企業選択";
    } else if (binderInfo.useMode === "import") {
      register = "Excelインポート";
    }

    // フィード条件
    if (
      binderInfo.includeRelatedCorp === "1" &&
      (binderInfo.andKeyword ||
        binderInfo.orKeyword ||
        binderInfo.notKeyword ||
        binderInfo.query) &&
      binderInfo.searchType === "0"
    ) {
      feedConditions = "企業またはキーワード";
    } else if (
      binderInfo.includeRelatedCorp === "1" &&
      (binderInfo.andKeyword ||
        binderInfo.orKeyword ||
        binderInfo.notKeyword ||
        binderInfo.query) &&
      binderInfo.searchType === "1"
    ) {
      feedConditions = "企業とキーワード";
    } else if (
      binderInfo.includeRelatedCorp === "1" &&
      !binderInfo.andKeyword &&
      !binderInfo.orKeyword &&
      !binderInfo.notKeyword &&
      !binderInfo.query
    ) {
      feedConditions = "企業";
    } else if (
      binderInfo.includeRelatedCorp === "0" &&
      (binderInfo.andKeyword ||
        binderInfo.orKeyword ||
        binderInfo.notKeyword ||
        binderInfo.query)
    ) {
      feedConditions = "キーワード";
    }

    return { count, register, feedConditions };
  };

  const history = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // ボタン押下後のアクション
  const onAction = async (binderInfo: BinderRequestParams | null = null) => {
    if (!binderActionState) return;

    const binderId = binderActionState.binderInfo?.binderId ?? "";

    // 作成・編集時はAPIを待って閉じる
    if (!["new", "edit"].includes(binderActionState.type)) {
      closeBinderActionModal();
      onClose();
    }

    switch (binderActionState.type) {
      // 削除
      case "delete":
      case "delete_return_index":
        const response = await deletePersonalBinder(binderId);
        trackAction("myCollectionDeleteComplete", response.binderId);
        await fetchPersonalBinders(limit);
        if (binderActionState.type === "delete_return_index") {
          history("/binder/personal", { state: { reserveToast: true } });
        }
        toast.setMessages(["コレクションを削除しました"]);
        break;
      // 複製
      case "clone":
      case "clone_show_new":
        const defaultCaption =
          binderActionState?.binderInfo?.caption + "のコピー";
        // コレクション名の複製処理
        const cloneBinderId = await cloneBinder(binderId, defaultCaption);
        // 複製アクション送信
        trackAction("myCollectionCloneComplete", cloneBinderId);
        // コレクション複製後、コレクション情報を取得し直す
        await fetchPersonalBinders(limit);
        if (binderActionState.type === "clone_show_new") {
          history(`/binder/${cloneBinderId}/article?type=personal`);
        } else {
          toast.setMessages(["コレクションを複製しました"]);
        }
        break;
      // 共有
      case "share":
        const sharedFlg = binderInfo?.setShare ?? false;
        await shareBinder(binderId, sharedFlg);
        trackAction("myCollectionShareSave", {
          id: binderId,
          events: sharedFlg
            ? "コレクション共有完了"
            : "コレクション共有解除完了"
        });
        // 画面を再読み込みし、共有マークの表示/非表示を行うため
        if (location.pathname.match(/\/binder\/[0-9]+/)) {
          // コレクション詳細ページの場合
          await fetchBinderList();
        } else {
          await fetchPersonalBinders(limit);
        }
        toast.setMessages([
          sharedFlg
            ? "コレクションを共有しました"
            : "コレクションの共有を解除しました"
        ]);
        break;
      // 公開
      case "publish":
        const publishedFlg = binderInfo?.setPublish ?? false;
        await publishBinder(binderId, publishedFlg);
        trackAction("myCollectionPublishComplete", binderId);
        // 画面を再読み込みし、公開マークの表示/非表示を行うため
        if (location.pathname.match(/\/binder\/[0-9]+/)) {
          // コレクション詳細ページの場合
          await fetchBinderList();
        } else {
          // 画面を再読み込みし、公開マークの表示/非表示を行うため
          await fetchPersonalBinders(limit);
        }
        toast.setMessages([
          publishedFlg
            ? "コレクションを公開しました"
            : "コレクションの公開を解除しました"
        ]);
        break;
      // 新規作成
      case "new":
        if (!binderInfo) break;
        resetErrors();
        const createParams = { ...binderInfo };
        delete createParams.useMode;
        const createResult = await createBinder(createParams);
        if (!createResult.success) {
          setErrors(createResult.messages);
          return;
        }
        closeBinderActionModal();
        onClose();
        trackAction("myCollectionSave", {
          binderId: createResult.binderId,
          type: "コレクション作成完了",
          count: getSaveAtlasInfo(binderInfo).count,
          register: getSaveAtlasInfo(binderInfo).register,
          feedConditions: getSaveAtlasInfo(binderInfo).feedConditions
        });
        await fetchPersonalBinders(limit);
        toast.setMessages(["コレクションを作成しました"]);
        break;
      // 編集
      case "edit":
        if (!binderInfo) break;
        resetErrors();
        const updateParams = { ...binderInfo };
        delete updateParams.useMode;
        const updateResult = await updateBinder(
          updateParams,
          binderId,
          closeBinderActionModal
        );
        if (!updateResult.success) {
          setErrors(updateResult.messages);
          return;
        }
        trackAction("myCollectionSave", {
          binderId: updateResult.binderId,
          type: "コレクション編集完了",
          count: getSaveAtlasInfo(binderInfo).count,
          register: getSaveAtlasInfo(binderInfo).register,
          feedConditions: getSaveAtlasInfo(binderInfo).feedConditions
        });
        toast.setMessages(["コレクションを編集しました"]);
        break;
    }
  };

  const modalType = binderActionState
    ? getModalType(binderActionState.type)
    : "new";

  const checkReOpenFlag = () => {
    if (reOpenFlag) {
      setReOpenFlag(false);
    }
    return reOpenFlag;
  };

  useEffect(() => {
    setErrors(errors);
  }, [errors]);

  return (
    <>
      <Modal
        isOpen={isOpenModal}
        uncontrolled
        isPreventingClearMessage
        modalClassName={
          modalType === "delete" ? styles.deleteModal : styles.modal
        }
        title={modalTexts[modalType].title}
        qsFlag={modalType === "new" || modalType === "edit"}
        onClose={() => {
          if (
            isOpenModal &&
            binderActionState &&
            (modalType === "new" || modalType === "edit")
          ) {
            if (isOpenCompanyModal || isOpenImportModal) return true;

            setIsOpenConfirmModal(true);
            // 共通モーダルを閉じなかったこととして返す
            return true;
          } else if (modalType === "clone") {
            setCloneCaption("");
          }
          setNotDeletable(true);
          closeBinderActionModal();
          onClose();
        }}
        checkReOpenFlag={checkReOpenFlag}
        appendix={
          isOpenHelp && (
            <AdvancedSearch
              isInModal={true}
              onClose={() => setIsOpenHelp(false)}
            />
          )
        }
      >
        {modalType === "clone" && (
          <div className={styles.nameInput}>
            <div className={styles.inputLabel}>コレクション名</div>
            <div>
              <TextInput
                placeholder={
                  binderActionState?.binderInfo?.caption + "のコピー"
                }
                value={cloneCaption}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCloneCaption(e.target.value);
                }}
              />
            </div>
          </div>
        )}
        {modalType === "new" ||
        modalType === "edit" ||
        modalType === "share" ||
        modalType === "publish" ? (
          <BinderForm
            menuType={modalType}
            binderInfo={binderActionState?.binderInfo}
            onAction={onAction}
            handleSave={handleSave}
            isSaveLoading={isSaveLoading}
            isOpenCompanyModal={isOpenCompanyModal}
            isOpenImportModal={isOpenImportModal}
            setOpenCompanyModal={isOpen => setIsOpenCompanyModal(isOpen)}
            setOpenImportModal={isOpen => setIsOpenImportModal(isOpen)}
            error={{
              definition: definitionError,
              background: backgroundError,
              task: taskError,
              technology: technologyError,
              topics: topicsError,
              other: otherError,
              regulation: regulationError
            }}
            setIsOpenHelp={setIsOpenHelp}
            setIsOpenDetailModal={setIsOpenDetailModal}
            setOtherError={setOtherError}
          />
        ) : (
          <>
            {modalType === "delete" && (
              <div>
                <div className={styles.deleteLabel}>削除するコレクション</div>
                <div
                  className={styles.collectionName}
                  data-testid="BinderPage-Common-BinderActionModal-collectionName"
                >
                  {binderActionState?.binderInfo?.caption}
                </div>
              </div>
            )}
            <div
              className={
                modalType === "delete" ? styles.deleteContent : styles.content
              }
            >
              {modalType === "delete" && (
                <div className={styles.deleteLabel}>注意点</div>
              )}
              {modalTexts[modalType].detail}
            </div>
            {modalType === "delete" && (
              <>
                <div className={styles.deleteLabel}>本当に削除しますか？</div>
                <div className={styles.deleteConfirmText}>
                  ・誤操作防止のため、削除するコレクションの名称を入力してください。
                </div>
                <div>
                  <TextInput
                    placeholder="削除するコレクションの名前"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setNotDeletable(
                        event.target.value !==
                          binderActionState?.binderInfo?.caption
                      );
                    }}
                    entireText={true}
                  />
                </div>
              </>
            )}
            <div className={styles.buttonWrapper}>
              <Button
                color="navy"
                onClick={() => {
                  closeBinderActionModal();
                  onClose();
                  if (modalType === "clone") {
                    setCloneCaption("");
                  }
                }}
                data-testid="BinderPage-Common-BinderActionModal-closeButton"
              >
                閉じる
              </Button>
              <Button
                color={modalType === "delete" ? "red" : "orange"}
                onClick={() => {
                  onAction();
                }}
                disabled={modalType === "delete" && notDeletable}
                data-testid="BinderPage-Common-BinderActionModal-actionModal"
              >
                {modalTexts[modalType].button}
              </Button>
            </div>
          </>
        )}
      </Modal>

      <CloseConfirmModal
        isOpen={isOpenConfirmModal}
        keepButtonText="入力を再開"
        onClickKeepButton={() => {
          setIsOpenConfirmModal(false);
          setReOpenFlag(true);
        }}
        onClickCloseButton={() => {
          setIsOpenConfirmModal(false);
          resetErrors();
          closeBinderActionModal();
          onClose();
        }}
      />
      <OverseasPremiumModal
        isOpen={isOpenDetailModal}
        onClose={() => {
          setIsOpenDetailModal(false);
        }}
      />
    </>
  );
};
